import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import ResponsivenessBadge from "../../components/ResponsivenessBadge";
import { Search, ArrowRight, Trophy, MapPin, Sparkles, BadgeCheck, Lock, Youtube, Users } from "lucide-react";
import { isBrandPro } from "../../lib/plan";

const SPORT_QUICK = ["", "Football", "Athletics", "MMA / Combat", "Cycling", "Basketball", "Rugby"];

export default function Directory() {
  const { user } = useAuth();
  const isPro = isBrandPro(user);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ sport: "", q: "", location: "" });
  // Brand-Pro-only filters. Disabled for non-Pro users (lock icon shown).
  const [proFilters, setProFilters] = useState({
    min_audience: "", max_audience: "", min_engagement: "",
    min_female_pct: "", min_age_18_24: "",
    has_youtube: false, has_agency: false, verified_only: false,
  });
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.sport) params.sport = filters.sport;
    if (filters.q) params.q = filters.q;
    if (filters.location) params.location = filters.location;
    if (isPro) {
      Object.entries(proFilters).forEach(([k, v]) => {
        if (v === "" || v === false || v === null || v === undefined) return;
        params[k] = v;
      });
    }
    api.get("/directory/athletes", { params })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const updPro = (k, v) => setProFilters(p => ({ ...p, [k]: v }));

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12">
        <div className="mb-10">
          <div className="label-eyebrow">Discover</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="directory-title">Athlete Directory</h1>
          <p className="text-ink-muted mt-2 text-sm">Every PITCHD athlete who hasn&apos;t opted out. Pro members unlock verified demographic filters.</p>
        </div>

        <div className="surface p-5 mb-6" data-testid="directory-filters">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
              <input value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && load()}
                placeholder="Search by name, sport, club…" data-testid="directory-search-input"
                className="w-full bg-bg border border-line focus:border-volt outline-none pl-10 pr-3 py-2.5 text-white text-sm" />
            </div>
            <input value={filters.location} onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && load()}
              placeholder="Location (e.g. UK, London, Saudi…)" data-testid="directory-location-input"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />
            <button onClick={load} className="btn-volt text-sm" data-testid="directory-apply-btn">Apply filters</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {SPORT_QUICK.map(s => (
              <button key={s || "all"} onClick={() => { setFilters(f => ({ ...f, sport: s })); setTimeout(load, 0); }}
                data-testid={`directory-quick-${s || "all"}`}
                className={`px-3 py-1.5 border text-[10px] uppercase tracking-widest ${filters.sport === s ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>
                {s || "All sports"}
              </button>
            ))}
          </div>

          {/* Brand-Pro advanced filter strip */}
          <div className="border-t border-line mt-5 pt-5">
            <button onClick={() => setShowAdvanced(s => !s)}
              data-testid="directory-advanced-toggle"
              className="text-xs uppercase tracking-widest text-volt hover:text-white inline-flex items-center gap-2">
              {showAdvanced ? "Hide" : "Show"} advanced filters
              {!isPro && <Lock className="w-3 h-3 text-ink-muted" />}
            </button>
            {showAdvanced && (
              <div className={`mt-4 grid sm:grid-cols-2 md:grid-cols-3 gap-3 ${!isPro ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="directory-advanced-filters">
                <NumberFilter label="Min audience" value={proFilters.min_audience} onChange={v => updPro("min_audience", v)} testid="filter-min-audience" />
                <NumberFilter label="Max audience" value={proFilters.max_audience} onChange={v => updPro("max_audience", v)} testid="filter-max-audience" />
                <NumberFilter label="Min engagement %" value={proFilters.min_engagement} onChange={v => updPro("min_engagement", v)} testid="filter-min-engagement" step="0.1" />
                <NumberFilter label="Min female audience %" value={proFilters.min_female_pct} onChange={v => updPro("min_female_pct", v)} testid="filter-min-female" />
                <NumberFilter label="Min 18-24 audience %" value={proFilters.min_age_18_24} onChange={v => updPro("min_age_18_24", v)} testid="filter-min-age-18-24" />
                <div className="flex items-center gap-2 flex-wrap pt-2">
                  <ToggleFilter label="YouTube ✓" checked={proFilters.has_youtube} onChange={v => updPro("has_youtube", v)} testid="filter-has-youtube" />
                  <ToggleFilter label="Agency ✓" checked={proFilters.has_agency} onChange={v => updPro("has_agency", v)} testid="filter-has-agency" />
                  <ToggleFilter label="Verified only" checked={proFilters.verified_only} onChange={v => updPro("verified_only", v)} testid="filter-verified-only" />
                </div>
              </div>
            )}
            {showAdvanced && !isPro && (
              <div className="text-xs text-volt mt-3" data-testid="directory-advanced-upsell">
                Advanced filters are a Brand Pro feature. <Link to="/app/pricing" className="underline">Upgrade →</Link>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="surface p-12 text-center text-ink-muted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="surface p-12 text-center">
            <div className="font-heading text-2xl uppercase">No athletes match your filters yet</div>
            <p className="text-ink-muted text-sm mt-2">PITCHD is in early access — directory grows as athletes opt-in. Try removing a filter.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-results">
            {items.map(a => <AthleteCard key={a.athlete_user_id} a={a} />)}
          </div>
        )}
      </div>
    </BrandDashboardLayout>
  );
}

function NumberFilter({ label, value, onChange, testid, step = "1" }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}</div>
      <input type="number" step={step} value={value} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
    </label>
  );
}

function ToggleFilter({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`text-[11px] uppercase tracking-widest border px-3 py-2 ${checked ? "border-volt text-volt bg-volt/10" : "border-line text-ink-muted"}`}>
      {label}
    </button>
  );
}

function AthleteCard({ a }) {
  return (
    <Link to={`/app/brand/athlete/${a.athlete_user_id}`} className="surface p-5 hover:border-volt transition-colors group" data-testid={`athlete-card-${a.athlete_user_id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="font-heading text-xl uppercase tracking-tight truncate" data-testid={`athlete-name-${a.athlete_user_id}`}>{a.full_name || "Unnamed athlete"}</div>
          <div className="text-xs text-ink-muted mt-1">{a.sport} {a.level && `· ${a.level}`}</div>
        </div>
        {a.positioning_score !== null && a.positioning_score !== undefined && (
          <div className="text-right shrink-0 ml-3">
            <div className="text-volt font-mono text-2xl leading-none" data-testid={`athlete-score-${a.athlete_user_id}`}>{a.positioning_score}</div>
            <div className="text-[9px] uppercase tracking-widest text-ink-muted mt-1">Score</div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {a.has_youtube_connected && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-volt border border-volt/40 px-2 py-1">
            <Youtube className="w-3 h-3" /> Verified
          </span>
        )}
        {a.has_verified_agency && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-volt border border-volt/40 px-2 py-1">
            <BadgeCheck className="w-3 h-3" /> {a.agency_name || "Agency"}
          </span>
        )}
        {a.verified_demographics && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-ink-muted border border-line px-2 py-1">
            <Users className="w-3 h-3" /> Demographics
          </span>
        )}
      </div>
      <div className="mt-3 space-y-1.5 text-xs">
        {a.location && (<div className="flex items-center gap-2 text-ink-muted"><MapPin className="w-3 h-3" /> {a.location}</div>)}
        {a.club && (<div className="flex items-center gap-2 text-ink-muted"><Trophy className="w-3 h-3" /> {a.club}</div>)}
        {a.audience_size_label && (<div className="flex items-center gap-2 text-ink-muted"><Sparkles className="w-3 h-3" /> {a.audience_size_label} audience</div>)}
      </div>
      {a.target_sponsor_categories?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-4">
          {a.target_sponsor_categories.slice(0, 3).map(c => (
            <span key={c} className="text-[9px] uppercase tracking-widest border border-line px-2 py-1 text-ink-muted">{c}</span>
          ))}
        </div>
      )}
      {a.responsiveness && a.responsiveness.band !== "new" && (
        <div className="mt-3">
          <ResponsivenessBadge r={a.responsiveness} size="sm" inline testid={`athlete-responsiveness-${a.athlete_user_id}`} />
        </div>
      )}
      <div className="text-volt text-xs uppercase tracking-widest mt-5 inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
        View profile <ArrowRight className="w-3 h-3" />
      </div>
    </Link>
  );
}
